/**
 * royale-membership.js
 * Royale Reserve Benefits Widget — Phase 1
 *
 * Phase 1 scope: module structure and initialization only.
 * No reward calculations, no cart fetching, no AJAX logic.
 *
 * Future phases will extend this module:
 *   - Phase 2: store credit calculation via cart subtotal
 *   - Phase 3: free shipping threshold check
 *   - Phase 4: Appstle Memberships API integration
 *
 * Pattern: singleton IIFE module exposed on window.RoyaleMembership
 * so any future script can call RoyaleMembership.init() safely.
 */

;(function () {
  'use strict'

  /**
   * Selectors — single source of truth for DOM targeting.
   * Phase 2+ will add selectors here as new data-* attributes are needed.
   */
  var SELECTORS = {
    wrapper:        '[data-royale-benefits]',
    valueCredit:    '[data-royale-value="store-credit"]',
    valueShipping:  '[data-royale-value="free-shipping"]',
    valueAccess:    '[data-royale-value="early-access"]'
  }

  /**
   * Events — Expanse cart event names this module will listen to in Phase 2+.
   * Defined here so Phase 2 only needs to uncomment and implement handlers.
   */
  var CART_EVENTS = {
    cartUpdated:   'cartUpdated',
    cartDrawerOpen: 'CartDrawer:open'
  }

  /**
   * isRoyaleReserveMember
   * JavaScript counterpart to the Liquid membership detection.
   * Reads the data-royale-member attribute set by the snippet,
   * which itself is the authoritative server-side check.
   *
   * Phase 2+ will call this before any calculation to guard against
   * running logic for non-members.
   *
   * @param {HTMLElement} wrapper - The [data-royale-benefits] element
   * @returns {boolean}
   */
  function isRoyaleReserveMember(wrapper) {
    if (!wrapper) return false
    return wrapper.dataset.royaleMember === 'true'
  }

  /**
   * getWidget
   * Returns the widget wrapper element or null if not present in DOM.
   * Safe to call at any time — returns null gracefully when non-members
   * are viewing the cart (snippet renders nothing for non-members).
   *
   * @returns {HTMLElement|null}
   */
  function getWidget() {
    return document.querySelector(SELECTORS.wrapper)
  }

  /**
   * RoyaleMembership module
   */
  var RoyaleMembership = {

    /**
     * init
     * Entry point. Safe to call multiple times — guards against
     * double-initialization with _initialized flag.
     *
     * Phase 2+ will register cart event listeners here.
     */
    init: function () {
      if (RoyaleMembership._initialized) return

      var widget = getWidget()

      if (!widget) {
        return
      }

      if (!isRoyaleReserveMember(widget)) {
        return
      }

      RoyaleMembership._widget = widget
      RoyaleMembership._initialized = true

      /**
       * Phase 2 hook: uncomment to subscribe to cart updates.
       *
       * document.addEventListener(CART_EVENTS.cartUpdated, function (evt) {
       *   RoyaleMembership.onCartUpdated(evt.detail.cart)
       * })
       *
       * document.addEventListener(CART_EVENTS.cartDrawerOpen, function () {
       *   RoyaleMembership.onDrawerOpen()
       * })
       */
    },

    /**
     * onCartUpdated — Phase 2 stub
     * Will receive the updated cart object and trigger value recalculations.
     *
     * @param {Object} cart - Shopify cart JSON object
     */
    onCartUpdated: function (cart) {
      // Phase 2: implement store credit and shipping calculations here
    },

    /**
     * onDrawerOpen — Phase 2 stub
     * Will refresh benefit values when the cart drawer opens.
     */
    onDrawerOpen: function () {
      // Phase 2: refresh displayed values here
    },

    /**
     * Expose helpers for external use / testing
     */
    isRoyaleReserveMember: isRoyaleReserveMember,
    getWidget: getWidget,
    SELECTORS: SELECTORS,
    CART_EVENTS: CART_EVENTS,

    _initialized: false,
    _widget: null
  }

  /**
   * Auto-initialize when DOM is ready.
   * Uses DOMContentLoaded if document is still loading,
   * otherwise calls immediately (handles deferred script execution).
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      RoyaleMembership.init()
    })
  } else {
    RoyaleMembership.init()
  }

  window.RoyaleMembership = RoyaleMembership

})()

/**
 * ============================================================
 * RoyaleMembershipConversion
 * Membership Conversion Widget — Phase 3 (Polish)
 *
 * Live savings calculation engine for the cart drawer.
 * Shown to guests and non-members only.
 *
 * Hooks into Expanse native events (sourced from assets/events.js):
 *   - 'cart:updated'      → qty change / item remove (full cart JSON in detail)
 *   - 'ajaxProduct:added' → product added (section HTML only → we fetch cart.js)
 *   - 'CartDrawer:open'   → drawer opened (uses cached cart or re-fetches)
 *
 * All config values read from data-* attributes set by Liquid from Theme Settings.
 * Zero hardcoded business logic values in this file.
 *
 * Extension points (Phase 4+):
 *   RoyaleMembershipConversion.hooks.beforeUpdate(cart, savings, config)
 *   RoyaleMembershipConversion.hooks.afterUpdate(savings, config)
 *   RoyaleMembershipConversion.hooks.onError(err)
 * ============================================================
 */
;(function () {
  'use strict'

  /* ============================================================
     Constants
     ============================================================ */

  /**
   * Selectors — single source of truth for all DOM targeting.
   * Add new selectors here when Phase 4+ features need new DOM slots.
   */
  var SELECTORS = {
    wrapper:          '[data-royale-conversion]',
    card:             '[data-royale-conversion] .royale-conv-card',
    cta:              '[data-royale-conv-cta]',
    totalBanner:      '[data-royale-conv-total]',
    valueTotal:       '[data-royale-conv-value="total"]',
    valueStoreCredit: '[data-royale-conv-value="store-credit"]',
    valueEarlyAccess: '[data-royale-conv-value="early-access"]',
    message:          '[data-royale-conv-message]'
    /**
     * Phase 4 extension slots — add selectors when DOM elements are added:
     * creditBalance:    '[data-royale-conv-value="credit-balance"]',
     * lifetimeSavings:  '[data-royale-conv-value="lifetime-savings"]',
     * vipTier:          '[data-royale-conv-value="vip-tier"]',
     */
  }

  /**
   * Expanse native cart event names — verified from assets/events.js.
   * IMPORTANT: cartUpdated is 'cart:updated' NOT 'cartUpdated'.
   */
  var CART_EVENTS = {
    cartUpdated:      'cart:updated',
    ajaxProductAdded: 'ajaxProduct:added',
    cartDrawerOpen:   'CartDrawer:open'
  }

  /**
   * CSS modifier classes for value state management.
   * Centralised here to prevent string literals scattered in logic.
   */
  var CSS = {
    loading:        'royale-conv-benefit__value--loading',
    included:       'royale-conv-benefit__value--included',
    amount:         'royale-conv-benefit__value--amount',
    calculating:    'royale-conv-benefit__value--calculating',
    updating:       'royale-conv-benefit__value--updating',
    totalVisible:   'royale-conv-total--visible',
    totalLoading:   'royale-conv-total--loading'
  }

  /** All mutable value state classes — used by clearValueClasses() */
  var VALUE_STATE_CLASSES = [
    CSS.loading, CSS.included, CSS.amount, CSS.calculating, CSS.updating
  ]

  /* ============================================================
     Utility Helpers
     ============================================================ */

  /**
   * isRoyaleReserveMember
   * Reads data-royale-member on the wrapper set by Liquid (server-side authority).
   * The conversion widget snippet does not render for members, so this will
   * always return false in normal operation — kept as a safety guard.
   *
   * @param {HTMLElement} wrapper
   * @returns {boolean}
   */
  function isRoyaleReserveMember(wrapper) {
    if (!wrapper) return false
    return wrapper.dataset.royaleMember === 'true'
  }

  /**
   * getWidget
   * Returns the conversion widget wrapper element or null.
   * Safe to call at any time — null means the snippet did not render
   * (either member, or widget disabled in Theme Settings).
   *
   * @returns {HTMLElement|null}
   */
  function getWidget() {
    return document.querySelector(SELECTORS.wrapper)
  }

  /**
   * showWidget
   * @param {HTMLElement} wrapper
   */
  function showWidget(wrapper) {
    if (!wrapper) return
    wrapper.removeAttribute('hidden')
    wrapper.setAttribute('aria-hidden', 'false')
  }

  /**
   * hideWidget
   * Does NOT remove from DOM — preserves element for potential re-renders.
   * @param {HTMLElement} wrapper
   */
  function hideWidget(wrapper) {
    if (!wrapper) return
    wrapper.setAttribute('hidden', '')
    wrapper.setAttribute('aria-hidden', 'true')
  }

  /**
   * clearValueClasses
   * Removes all mutable state modifier classes from a value element.
   * Replaces the fragile string-replace approach used in Phase 2.
   *
   * @param {HTMLElement} el
   */
  function clearValueClasses(el) {
    if (!el) return
    VALUE_STATE_CLASSES.forEach(function (cls) {
      el.classList.remove(cls)
    })
  }

  /**
   * setTextIfChanged
   * Only writes textContent when the value actually changes.
   * Prevents unnecessary DOM repaints and duplicate aria-live announcements.
   *
   * @param {HTMLElement} el
   * @param {string} text
   */
  function setTextIfChanged(el, text) {
    if (!el || el.textContent === text) return
    el.textContent = text
  }

  /**
   * prefersReducedMotion
   * Checks the user's motion preference once and caches the result.
   * @returns {boolean}
   */
  var _prefersReducedMotion = null
  function prefersReducedMotion() {
    if (_prefersReducedMotion === null) {
      _prefersReducedMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
    return _prefersReducedMotion
  }

  /**
   * animateValue
   * Applies a brief CSS transition class when a value changes so numbers
   * fade out and in rather than snapping. Respects prefers-reduced-motion.
   * Duration matches the CSS transition: 200ms.
   *
   * @param {HTMLElement} el
   * @param {string} newText
   * @param {string} finalClass  - CSS state class to apply after transition
   */
  function animateValue(el, newText, finalClass) {
    if (!el) return

    if (prefersReducedMotion()) {
      clearValueClasses(el)
      el.classList.add(finalClass)
      setTextIfChanged(el, newText)
      return
    }

    clearValueClasses(el)
    el.classList.add(CSS.updating)

    setTimeout(function () {
      clearValueClasses(el)
      el.classList.add(finalClass)
      setTextIfChanged(el, newText)
    }, 180)
  }

  /**
   * formatMoney
   * Converts a cents integer to a formatted money string.
   * Uses window.Shopify.money_format when available for locale accuracy,
   * falls back to $X.XX.
   *
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '—'
    var dollars = (cents / 100).toFixed(2)

    try {
      if (window.Shopify && window.Shopify.money_format) {
        return window.Shopify.money_format
          .replace('{{amount}}', dollars)
          .replace('{{amount_no_decimals}}', Math.round(cents / 100))
          .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
          .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100))
      }
    } catch (e) {}

    return '$' + dollars
  }

  /* ============================================================
     Configuration
     ============================================================ */

  /**
   * readConfig
   * Reads all business logic parameters from data-* attributes on the wrapper.
   * Written by Liquid from Theme Settings — no hardcoded values in JS.
   *
   * @param {HTMLElement} wrapper
   * @returns {Object}
   */
  function readConfig(wrapper) {
    return {
      creditRate:            parseFloat(wrapper.dataset.royaleConvCreditRate) || 5,
      shippingAmount:        parseInt(wrapper.dataset.royaleConvShippingAmount, 10) || 1500,
      freeShippingThreshold: parseInt(wrapper.dataset.royaleConvFreeShippingThreshold, 10) || 0,
      membershipProductTag:  (wrapper.dataset.royaleConvMembershipProductTag || 'Royale Reserve Membership').toLowerCase(),
      productUrl:            wrapper.dataset.royaleProductUrl || '',
      defaultZip:            wrapper.dataset.royaleConvDefaultZip || '60601',
      defaultProvince:       wrapper.dataset.royaleConvDefaultProvince || 'IL',
      defaultCountry:        wrapper.dataset.royaleConvDefaultCountry || 'US'
    }
  }

  /* ============================================================
     Item Filtering
     ============================================================ */

  /**
   * isExcludedItem
   * Returns true for line items that must be excluded from the eligible subtotal.
   * Excluded: membership products (by tag), gift cards (by product_type).
   *
   * @param {Object} item          - Shopify cart line item
   * @param {string} membershipTag - lowercase tag string
   * @returns {boolean}
   */
  function isExcludedItem(item, membershipTag) {
    if (!item) return true

    var productType = (item.product_type || '').toLowerCase()
    if (productType === 'gift cards' || productType === 'gift card') return true

    var tags = item.product_tags || item.tags || ''
    if (typeof tags === 'string') {
      if (tags.toLowerCase().indexOf(membershipTag) !== -1) return true
    } else if (Array.isArray(tags)) {
      for (var i = 0; i < tags.length; i++) {
        if ((tags[i] || '').toLowerCase() === membershipTag) return true
      }
    }

    return false
  }

  /* ============================================================
     Calculation Engine
     ============================================================ */

  /**
   * calculateEligibleSubtotal
   * Sums final_line_price for all non-excluded line items.
   * Taxes and shipping are excluded (checkout-calculated, not in cart JSON).
   *
   * @param {Object} cart
   * @param {string} membershipTag
   * @returns {number} cents
   */
  function calculateEligibleSubtotal(cart, membershipTag) {
    if (!cart || !Array.isArray(cart.items)) return 0
    return cart.items.reduce(function (sum, item) {
      if (isExcludedItem(item, membershipTag)) return sum
      return sum + (item.final_line_price || 0)
    }, 0)
  }

  /**
   * calculateStoreCredit
   * credit = round(eligibleSubtotal × creditRate / 100)
   *
   * @param {Object} cart
   * @param {Object} config
   * @returns {number} cents
   */
  function calculateStoreCredit(cart, config) {
    var subtotal = calculateEligibleSubtotal(cart, config.membershipProductTag)
    if (subtotal <= 0) return 0
    return Math.round(subtotal * (config.creditRate / 100))
  }

  /**
   * calculateTotalSavings
   * @param {number} shippingSavings cents
   * @param {number} storeCredit     cents
   * @returns {number} cents
   */
  function calculateTotalSavings(shippingSavings, storeCredit) {
    return shippingSavings + storeCredit
  }

  /* ============================================================
     Contextual Messaging
     ============================================================ */

  /**
   * getContextualMessage
   * Returns the most relevant single-line message for the current cart state.
   * Prioritised: threshold nudge → high-value → store credit fallback.
   *
   * Thresholds (in cents):
   *   - "near threshold"  → cart is within $20 of the free-shipping threshold
   *   - "high value"      → total membership value ≥ $40
   *
   * @param {Object} cart
   * @param {Object} savings  - { shipping, storeCredit, total }
   * @param {Object} config
   * @returns {string}
   */
  function getContextualMessage(cart, savings, config) {
    var threshold = config.freeShippingThreshold
    var nearThresholdWindow = 2000 /* $20.00 in cents */
    var highValueThreshold  = 4000 /* $40.00 in cents */

    /* Nudge: customer is close to the free-shipping threshold */
    if (
      threshold > 0 &&
      cart.total_price < threshold &&
      (threshold - cart.total_price) <= nearThresholdWindow
    ) {
      var gap = formatMoney(threshold - cart.total_price)
      return "You\u2019re only " + gap + " away from FREE Shipping as a member."
    }

    /* High-value order — reinforce the reward */
    if (savings.total >= highValueThreshold) {
      return "This order makes Royale Reserve even more valuable."
    }

    /* Default: store credit prompt */
    if (savings.storeCredit > 0) {
      return "Earn store credit on every eligible purchase."
    }

    return "See how much you could save with today\u2019s order."
  }

  /* ============================================================
     DOM Updates
     ============================================================ */

  /**
   * setLoadingState
   * Immediately shows loading placeholders on all calculated slots
   * when a cart event fires — prevents stale values being visible
   * during the async calculation window.
   *
   * @param {HTMLElement} wrapper
   */
  function setLoadingState(wrapper) {
    var slots = [
      wrapper.querySelector(SELECTORS.valueStoreCredit),
      wrapper.querySelector(SELECTORS.valueTotal)
    ]

    slots.forEach(function (el) {
      if (!el) return
      clearValueClasses(el)
      el.classList.add(CSS.loading)
      el.textContent = '\u2014'
    })

    var totalBanner = wrapper.querySelector(SELECTORS.totalBanner)
    if (totalBanner) {
      totalBanner.classList.remove(CSS.totalVisible)
      totalBanner.classList.add(CSS.totalLoading)
    }
  }

  /**
   * updateWidget
   * Writes calculated savings to DOM slots using targeted, animated updates.
   * Never rebuilds the widget — only modifies individual text nodes and classes.
   *
   * @param {HTMLElement} wrapper
   * @param {Object}      savings  - { shipping, storeCredit, total }
   * @param {Object}      config
   * @param {Object}      cart     - full Shopify cart JSON (for messaging)
   */
  function updateWidget(wrapper, savings, config, cart) {
    var elCredit    = wrapper.querySelector(SELECTORS.valueStoreCredit)
    var elTotal     = wrapper.querySelector(SELECTORS.valueTotal)
    var totalBanner = wrapper.querySelector(SELECTORS.totalBanner)
    var elMessage   = wrapper.querySelector(SELECTORS.message)

    /* ---- Store Credit ---- */
    if (elCredit) {
      var creditText, creditClass

      if (savings.storeCredit > 0) {
        creditText  = 'Earn ' + formatMoney(savings.storeCredit)
        creditClass = CSS.amount
      } else {
        creditText  = 'Add items to calculate'
        creditClass = CSS.calculating
      }

      animateValue(elCredit, creditText, creditClass)
    }

    /* ---- Total Banner ---- */
    if (elTotal && totalBanner) {
      totalBanner.classList.remove(CSS.totalLoading)
      if (savings.total > 0) {
        totalBanner.classList.add(CSS.totalVisible)
        animateValue(elTotal, formatMoney(savings.total), CSS.amount)
      } else {
        totalBanner.classList.remove(CSS.totalVisible)
        setTextIfChanged(elTotal, '\u2014')
      }
    }

    /* ---- Contextual Message ---- */
    if (elMessage && cart) {
      var msg = getContextualMessage(cart, savings, config)
      setTextIfChanged(elMessage, msg)
    }

    /* ---- Extension Hook: afterUpdate ---- */
    if (RoyaleMembershipConversion.hooks && typeof RoyaleMembershipConversion.hooks.afterUpdate === 'function') {
      try {
        RoyaleMembershipConversion.hooks.afterUpdate(savings, config)
      } catch (e) {
        if (typeof console !== 'undefined') console.warn('[RoyaleConversion] hooks.afterUpdate threw:', e)
      }
    }
  }

  /**
   * setErrorState
   * Graceful degradation when cart fetch or calculation fails.
   * Does not break the cart drawer — simply clears calculated values.
   *
   * @param {HTMLElement} wrapper
   */
  function setErrorState(wrapper) {
    var slots = [
      wrapper.querySelector(SELECTORS.valueStoreCredit)
    ]
    slots.forEach(function (el) {
      if (!el) return
      clearValueClasses(el)
      el.classList.add(CSS.calculating)
      el.textContent = '\u2014'
    })

    var totalBanner = wrapper.querySelector(SELECTORS.totalBanner)
    if (totalBanner) {
      totalBanner.classList.remove(CSS.totalVisible, CSS.totalLoading)
    }

    /* ---- Extension Hook: onError ---- */
    if (RoyaleMembershipConversion.hooks && typeof RoyaleMembershipConversion.hooks.onError === 'function') {
      try { RoyaleMembershipConversion.hooks.onError() } catch (e) {}
    }
  }

  /* ============================================================
     Cart Fetch
     ============================================================ */

  /**
   * fetchCart
   * Fetches latest cart JSON from /cart.js.
   * Required after ajaxProduct:added because that event only
   * carries rendered section HTML — not the cart JSON object.
   *
   * @returns {Promise<Object>}
   */
  function fetchCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/'

    return fetch(root + 'cart.js?t=' + Date.now(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch ' + res.status)
      return res.json()
    })
  }

  /* ============================================================
     CTA Button — Add to Cart
     ============================================================ */

  /**
   * initCTAButton
   * Wires the "Join Royale Reserve" button to add the membership variant
   * directly to cart via AJAX, then fires the theme's ajaxProduct:added
   * event so the cart drawer opens and refreshes automatically.
   *
   * @param {HTMLElement} wrapper
   */
  function initCTAButton(wrapper) {
    var btn = wrapper.querySelector(SELECTORS.cta)
    if (!btn) return

    var variantId = btn.dataset.variantId
    if (!variantId) return

    btn.addEventListener('click', function () {
      var btnText = btn.querySelector('.royale-conv-button__text')
      var originalText = btnText ? btnText.textContent : ''

      btn.disabled = true
      if (btnText) btnText.textContent = 'Adding\u2026'

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root
        : '/'

      fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }], sections: ['cart-ajax'] })
      })
        .then(function (res) { return res.json() })
        .then(function (data) {
          if (data.status === 422 || data.status === 'bad_request') {
            if (btnText) btnText.textContent = originalText
            btn.disabled = false
            if (data.description) alert(data.description)
          } else {
            if (btnText) btnText.textContent = 'Added to Cart \u2713'

            /* Fetch updated cart to refresh subtotal immediately */
            fetchCart().then(function (cart) {
              /* Update every [data-subtotal] in the page (drawer + cart page) */
              var subtotalEls = document.querySelectorAll('[data-subtotal]')
              subtotalEls.forEach(function (el) {
                el.textContent = formatMoney(cart.total_price)
              })

              /* Fire cart:updated so the theme and widget both refresh */
              document.dispatchEvent(new CustomEvent('cart:updated', {
                bubbles: true,
                detail: { cart: cart }
              }))

              /* Also fire ajaxProduct:added for cart drawer to open/refresh */
              document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                bubbles: true,
                detail: { product: data }
              }))
            }).catch(function () {
              /* Cart fetch failed — still fire events so drawer at least opens */
              document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                bubbles: true,
                detail: { product: data }
              }))
            })
          }
        })
        .catch(function (err) {
          if (typeof console !== 'undefined') console.warn('[RoyaleConversion] add membership failed:', err)
          if (btnText) btnText.textContent = originalText
          btn.disabled = false
        })
    })
  }

  /* ============================================================
     Core Calculation Pipeline
     ============================================================ */

  /**
   * runCalculations
   * Pipeline entry point for every cart change.
   * Total membership value = store credit only (shipping savings removed).
   *
   * @param {Object}      cart    - Shopify cart JSON
   * @param {HTMLElement} wrapper
   */
  function runCalculations(cart, wrapper) {
    if (!cart || !wrapper) return

    RoyaleMembershipConversion._lastCart = cart

    var config  = readConfig(wrapper)
    var credit  = calculateStoreCredit(cart, config)
    var savings = { storeCredit: credit, total: credit }

    /* ---- Extension Hook: beforeUpdate ---- */
    if (RoyaleMembershipConversion.hooks && typeof RoyaleMembershipConversion.hooks.beforeUpdate === 'function') {
      try {
        RoyaleMembershipConversion.hooks.beforeUpdate(cart, savings, config)
      } catch (e) {
        if (typeof console !== 'undefined') console.warn('[RoyaleConversion] hooks.beforeUpdate threw:', e)
      }
    }

    updateWidget(wrapper, savings, config, cart)
  }

  /* ============================================================
     RoyaleMembershipConversion Module
     ============================================================ */

  var RoyaleMembershipConversion = {

    /**
     * init
     * Entry point. Safe to call multiple times — _initialized flag prevents
     * duplicate event listeners and double-fetches.
     */
    init: function () {
      if (RoyaleMembershipConversion._initialized) return

      var widget = getWidget()
      if (!widget) return

      /* Safety guard — member check (Liquid is authoritative, this is defensive) */
      if (isRoyaleReserveMember(widget)) {
        hideWidget(widget)
        return
      }

      RoyaleMembershipConversion._widget = widget
      RoyaleMembershipConversion._initialized = true

      showWidget(widget)
      setLoadingState(widget)
      initCTAButton(widget)

      /* ---- Initial cart fetch ---- */
      fetchCart()
        .then(function (cart) { runCalculations(cart, widget) })
        .catch(function (err) {
          if (typeof console !== 'undefined') console.warn('[RoyaleConversion] init fetch failed:', err)
          setErrorState(widget)
        })

      /* ---- Event: quantity changed / item removed ---- */
      document.addEventListener(CART_EVENTS.cartUpdated, function (evt) {
        var cart = evt && evt.detail && evt.detail.cart
        if (!cart) return
        var w = getWidget()
        if (!w) return
        setLoadingState(w)
        runCalculations(cart, w)
      })

      /* ---- Event: product added via AJAX add-to-cart ---- */
      document.addEventListener(CART_EVENTS.ajaxProductAdded, function () {
        var w = getWidget()
        if (!w) return
        setLoadingState(w)
        fetchCart()
          .then(function (cart) { runCalculations(cart, w) })
          .catch(function (err) {
            if (typeof console !== 'undefined') console.warn('[RoyaleConversion] add fetch failed:', err)
            setErrorState(w)
          })
      })

      /* ---- Event: cart drawer opened ---- */
      document.addEventListener(CART_EVENTS.cartDrawerOpen, function () {
        var w = getWidget()
        if (!w) return
        /* Use cached cart to avoid an extra network request on every drawer open */
        if (RoyaleMembershipConversion._lastCart) {
          runCalculations(RoyaleMembershipConversion._lastCart, w)
        } else {
          setLoadingState(w)
          fetchCart()
            .then(function (cart) { runCalculations(cart, w) })
            .catch(function (err) {
              if (typeof console !== 'undefined') console.warn('[RoyaleConversion] drawer open fetch failed:', err)
              setErrorState(w)
            })
        }
      })
    },

    /* ============================================================
       Extension Hooks — Phase 4+
       Register callbacks here without modifying core logic.

       Usage example (Phase 4 Appstle integration):
         RoyaleMembershipConversion.hooks.afterUpdate = function(savings, config) {
           AppstleWidget.refresh(savings)
         }

       Available hooks:
         beforeUpdate(cart, savings, config) — called before DOM is updated
         afterUpdate(savings, config)        — called after DOM is updated
         onError()                           — called when cart fetch fails
    ============================================================ */
    hooks: {
      beforeUpdate: null,
      afterUpdate:  null,
      onError:      null
      /**
       * Future Phase 4+ hooks to add here:
       * onVipTierChange(tier)
       * onHighValueCart(total)
       * onMembershipProductDetected()
       */
    },

    /* ---- Public API (all functions exposed for testing) ---- */
    isRoyaleReserveMember:     isRoyaleReserveMember,
    getWidget:                 getWidget,
    showWidget:                showWidget,
    hideWidget:                hideWidget,
    clearValueClasses:         clearValueClasses,
    readConfig:                readConfig,
    isExcludedItem:            isExcludedItem,
    calculateEligibleSubtotal: calculateEligibleSubtotal,
    calculateStoreCredit:      calculateStoreCredit,
    calculateTotalSavings:     calculateTotalSavings,
    getContextualMessage:      getContextualMessage,
    updateWidget:              updateWidget,
    setLoadingState:           setLoadingState,
    setErrorState:             setErrorState,
    fetchCart:                 fetchCart,
    formatMoney:               formatMoney,
    animateValue:              animateValue,
    SELECTORS:                 SELECTORS,
    CART_EVENTS:               CART_EVENTS,
    CSS:                       CSS,

    /* ---- Private state ---- */
    _initialized: false,
    _widget:      null,
    _lastCart:    null
  }

  /* ---- Auto-initialize on DOM ready ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      RoyaleMembershipConversion.init()
    })
  } else {
    RoyaleMembershipConversion.init()
  }

  window.RoyaleMembershipConversion = RoyaleMembershipConversion

})()
